'use client';

import { use, useMemo, useState } from 'react';
import {
    getSchoolData,
    getSchoolName,
    SchoolUnit,
    CapitalDeposit,
    validUnits
} from '@/lib/data';
import {
    Building2,
    Search,
    ChevronDown,
    ChevronRight,
    TrendingUp,
    Info,
    X,
    Calendar,
    ArrowUpCircle,
    UserCircle,
    Eye,
    ShieldCheck,
    ArrowRightCircle
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';

export default function CapitalDepositPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const baseType = initialUnit.split('-')[0];
    const sisterUnits = validUnits.filter(u =>
        u.startsWith(baseType) &&
        (u.includes('-') || !validUnits.some(v => v.startsWith(baseType) && v.includes('-')))
    );

    const defaultUnit = initialUnit.includes('-')
        ? initialUnit as SchoolUnit
        : (sisterUnits.find(u => u.includes('-')) || initialUnit) as SchoolUnit;

    const [activeUnit, setActiveUnit] = useState<SchoolUnit>(defaultUnit);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDeposit, setSelectedDeposit] = useState<CapitalDeposit | null>(null);

    const schoolData = getSchoolData(activeUnit);
    const schoolName = getSchoolName(activeUnit);

    const camelCaseUnit = activeUnit.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    const deposits = (
        (schoolData as any)[`${camelCaseUnit}CapitalDeposits`] ||
        (schoolData as any)[`${activeUnit.split('-')[0]}CapitalDeposits`] ||
        []
    ) as CapitalDeposit[];

    const filteredDeposits = useMemo(() => {
        return deposits.filter(d =>
            (d.source || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (d.description || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [deposits, searchTerm]);

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header Area */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative z-10 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                                <Link href={`/yayasan/${initialUnit}/keuangan`} className="hover:text-primary transition-all">Keuangan</Link>
                                <ChevronRight className="w-3.5 h-3.5" />
                                <span className="text-foreground">Setoran Modal</span>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                    <TrendingUp className="w-8 h-8" />
                                </div>
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight whitespace-nowrap">Setoran Modal</h1>
                                    <p className="text-secondary font-medium flex items-center gap-2">
                                        <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Penguatan Ekuitas Unit {baseType.toUpperCase()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Unit Selector */}
                        <div className="relative min-w-[280px]">
                            <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block ml-1">Pilih Unit Sekolah</label>
                            <div className="relative group">
                                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                                <select
                                    className="w-full pl-11 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all appearance-none cursor-pointer outline-none shadow-sm"
                                    value={activeUnit}
                                    onChange={(e) => setActiveUnit(e.target.value as SchoolUnit)}
                                >
                                    {sisterUnits.map(u => (
                                        <option key={u} value={u}>{getSchoolName(u as SchoolUnit)}</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Modal Table Container */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col min-h-[400px]">
                <div className="p-6 border-b border-border-main flex flex-col md:flex-row md:items-center justify-between gap-4 bg-background/30">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari sumber modal atau keterangan..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-2xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Tanggal</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Sumber Modal</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Akun Tujuan</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Nominal</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main font-medium">
                            {filteredDeposits.length > 0 ? filteredDeposits.map((dep) => (
                                <tr key={dep.id} className="hover:bg-primary-soft/10 transition-colors group text-sm">
                                    <td className="px-8 py-5 whitespace-nowrap text-secondary font-bold tabular-nums">
                                        {dep.date}
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap">
                                        <p className="font-bold text-foreground underline decoration-primary/20 underline-offset-4">{dep.source}</p>
                                        <p className="text-[10px] text-muted truncate max-w-[250px] mt-0.5 uppercase tracking-widest font-bold">{dep.description}</p>
                                    </td>
                                    <td className="px-8 py-5 text-center whitespace-nowrap">
                                        <span className="text-[10px] font-bold text-primary bg-primary-soft px-3 py-1.5 rounded-lg border border-primary/20 uppercase tracking-widest italic">{dep.targetAccount}</span>
                                    </td>
                                    <td className="px-8 py-5 text-right whitespace-nowrap font-bold text-foreground tabular-nums text-base">
                                        {dep.amount}
                                    </td>
                                    <td className="px-8 py-5 text-right">
                                        <button
                                            onClick={() => setSelectedDeposit(dep)}
                                            className="p-2 rounded-xl bg-background border border-border-main text-muted hover:text-primary transition-all"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={5} className="px-8 py-32 text-center text-muted">
                                        <div className="flex flex-col items-center gap-4 opacity-10">
                                            <TrendingUp className="w-16 h-16" />
                                            <p className="font-bold text-sm uppercase tracking-widest">Tidak ada setoran modal</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Detail Modal */}
            {selectedDeposit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setSelectedDeposit(null)}></div>
                    <div className="relative bg-surface w-full max-w-lg rounded-4xl border border-border-main shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-border-main flex items-center justify-between bg-background/50">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary">
                                    <TrendingUp className="w-7 h-7" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-foreground">Detail Setoran Modal</h3>
                                    <p className="text-secondary text-xs font-medium uppercase tracking-widest mt-0.5">Capital Investment</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedDeposit(null)} className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-foreground hover:rotate-90 transition-all duration-300">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Tanggal Investasi</p>
                                    <p className="text-sm font-bold text-foreground">{selectedDeposit.date}</p>
                                </div>
                                <div className="space-y-1.5 text-right">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">ID Transaksi</p>
                                    <p className="text-sm font-bold text-foreground">{selectedDeposit.id}</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="p-5 bg-background border border-border-main rounded-3xl flex items-center gap-4">
                                    <UserCircle className="w-10 h-10 text-primary" />
                                    <div>
                                        <p className="text-[9px] font-bold text-muted uppercase tracking-widest">Sumber Modal</p>
                                        <p className="text-sm font-bold text-foreground">{selectedDeposit.source}</p>
                                    </div>
                                    <ArrowRightCircle className="w-5 h-5 text-muted ml-auto" />
                                    <div className="text-right">
                                        <p className="text-[9px] font-bold text-muted uppercase tracking-widest">Tujuan Unit</p>
                                        <p className="text-sm font-bold text-foreground">{schoolName}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 bg-primary/5 rounded-3xl border border-primary/10 text-center">
                                <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-1">Nominal Setoran Modal</p>
                                <p className="text-3xl font-bold text-primary tabular-nums tracking-tight">{selectedDeposit.amount}</p>
                            </div>

                            <div className="space-y-2">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Keterangan / Tujuan Modal</p>
                                <div className="p-4 bg-background border border-border-main rounded-2xl text-xs font-medium text-secondary leading-relaxed">
                                    {selectedDeposit.description}
                                </div>
                            </div>
                        </div>

                        <div className="p-8 bg-background/50 border-t border-border-main flex justify-end">
                            <button onClick={() => setSelectedDeposit(null)} className="px-8 py-3.5 bg-surface border border-border-main text-secondary rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-background transition-all">Tutup</button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
